import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { ExpectedError, GatedAppUserError } from "@/utilities/expectedErrors"
import { phoneValidationRule, requiredRule } from "@/utilities/form"
import { getAreaHierarchyIDs } from "@/utilities/getAreaHierarchyIDs"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useTheme } from "@/utilities/hooks/useTheme"

export const Route = createFileRoute(
  "/_authenticated/account/personal-information"
)({
  staticData: {
    pageName: "Personal Information",
  },
  component: RouteComponent,
})

interface PersonalInfoFormSchema {
  firstName: string
  lastName: string
  phone: string
}

function RouteComponent() {
  const { qrLookupID } = Route.useSearch()
  const { t } = useTranslation(["errors", "common"])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const { scannedArea } = useTheme()
  const locationID = scannedArea?.theme.locationInformation.locationID

  const form = useForm<PersonalInfoFormSchema>({
    defaultValues: {
      firstName: me?.inflowUser.first,
      lastName: me?.inflowUser.last,
      phone: me?.inflowUser.phone,
    },
    mode: "onBlur",
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Card>
      <Heading level={2}>
        {t("account.personalInfo.header", { ns: "common" })}
      </Heading>
      <Text style={{ marginTop: "var(--space-2)" }}>
        {t("account.personalInfo.descriptionPrefix", { ns: "common" })}
        &nbsp;
        <strong>{t("account.personalInfo.brand", { ns: "common" })}</strong>
        &nbsp;
        {t("account.personalInfo.descriptionSuffix", { ns: "common" })}
      </Text>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        aria-label={t("account.personalInfo.label", { ns: "common" })}
      >
        <fieldset disabled={isPending}>
          <div className="form-row">
            <Controller<PersonalInfoFormSchema>
              name="firstName"
              control={form.control}
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
              }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("account.personalInfo.firstName", { ns: "common" })}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    aria-label={t("account.personalInfo.firstName", {
                      ns: "common",
                    })}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<PersonalInfoFormSchema>
              name="lastName"
              control={form.control}
              rules={{ required: requiredRule(t("common.required")) }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("account.personalInfo.lastName", { ns: "common" })}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    aria-label={t("account.personalInfo.lastName", {
                      ns: "common",
                    })}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
          <Controller<PersonalInfoFormSchema>
            name="phone"
            control={form.control}
            rules={{
              validate: phoneValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("account.personalInfo.phoneNumber", { ns: "common" })}
                </Label>
                <TextField.PhoneWithCountrySelect
                  innerRef={ref}
                  defaultCountry="US"
                  aria-label={t("account.personalInfo.phoneNumber", {
                    ns: "common",
                  })}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button buttonType="primary" type="submit" isPending={isPending}>
            {t("account.personalInfo.save", { ns: "common" })}
          </Button>
        </fieldset>
      </form>
    </Card>
  )

  function onSubmit(data: PersonalInfoFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: PersonalInfoFormSchema) {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()

    if (!idToken || !me) {
      throw new Error("Missing authentication token or user")
    }

    const [_, rsp] = await Promise.all([
      api.auth.updateUserAttributes({
        given_name: data.firstName,
        family_name: data.lastName,
        phone_number: data.phone,
      }),
      api.appUser.PUT({
        cognitoIDToken: idToken,
        ...me.inflowUser,
        phone: data.phone,
        first: data.firstName,
        last: data.lastName,
        locationID: locationID || null,
        enterpriseID: locationID
          ? getAreaHierarchyIDs({ locationID }).enterpriseID
          : null,
      }),
    ])

    if ("failReason" in rsp) {
      throw new GatedAppUserError(rsp.failReason)
    }
  }

  function onSuccess() {
    queryClient.invalidateQueries({
      queryKey: meQueryOptions().queryKey,
      refetchType: "active",
    })
    navigate({
      to: "/account",
      search: { qrLookupID },
    })
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      form.setError("root", {
        message: t(error.translationKey, { ns: "errors" }),
      })
    } else {
      handleAuthError(error, form, t)
    }
  }
}
